import { captureException } from '@atlassian/error-handling/sentry';

const getMeta = (name: string): string | undefined => {
	if (typeof document === 'undefined') {
		return undefined;
	}
	const element = document.querySelector(`meta[name="${name}"]`);
	return element?.getAttribute('content') ?? undefined;
};

const extractFirstName = (fullName: string | undefined): string | undefined => {
	if (!fullName?.trim()) {
		return undefined;
	}
	const firstName = fullName.trim().split(' ')[0];
	return firstName || undefined;
};

export type CurrentUserProfile = {
	/** Profile picture URL, when the account has one. */
	avatarUrl: string | undefined;
	/** First name only, for greeting copy. */
	firstName: string | undefined;
};

type CurrentUserResponse = {
	name?: string;
	nickname?: string;
	picture?: string;
};

/**
 * Fetches the greeting name and profile picture for the current user in a single request.
 *
 * `/gateway/api/me` returns `picture` alongside `nickname`/`name`, so the avatar costs no extra
 * network call. Callers that only need the name should use `fetchUserName`.
 */
export const fetchCurrentUserProfile = async (): Promise<CurrentUserProfile> => {
	let userData: CurrentUserResponse | undefined;

	try {
		const response = await fetch('/gateway/api/me');
		if (response.ok) {
			userData = (await response.json()) as CurrentUserResponse;
		}
	} catch (error) {
		captureException('Failed to fetch user name from API', error as Error, {
			packageName: '@atlassian/twc-xflow-modal',
			teamName: 'starburst',
		});
	}

	const firstName =
		extractFirstName(userData?.nickname) ||
		extractFirstName(userData?.name) ||
		extractFirstName(getMeta('ajs-remote-user-fullname'));

	return {
		avatarUrl: userData?.picture,
		firstName,
	};
};

export const fetchUserName = async (): Promise<string | undefined> =>
	(await fetchCurrentUserProfile()).firstName;
